import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
} from "@mui/material";
import {
  VolunteerActivismOutlined as DonationIcon,
  ShoppingCartOutlined as SaleIcon,
  ConfirmationNumberOutlined as VoucherIcon,
  Clear as ClearIcon,
  LockOutlined as LockIcon,
  AccountBalanceWalletOutlined as WalletIcon,
} from "@mui/icons-material";
import { ArrowRight } from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton, CommonInput } from "../../../../Components/ui";

const GiftCardPurchaseCard = ({
  isDonation = false,
  tokenValue = "0",
  tokenName = "BET",
  price = "0",
  currency = "POL",
  voucherCode = "",
  onVoucherCodeChange,
  isVoucherInvalid = false,
  voucherErrorMessage = "",
  onSubmit,
  isSubmitting = false,
  isSold = false,
  isExpired = false,
  account = "",
  onConnectWallet,
}) => {
  const isActionDisabled = isSold || isExpired;

  return (
    <Card
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: { md: "sticky" },
        top: { md: 120 },
      }}
    >
      <Stack spacing={2.5}>
        {/* Header Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: borderRadiusPx.md,
              bgcolor: isDonation ? colors.amber[50] : colors.blue[50],
              color: isDonation ? colors.amber[800] : colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isDonation ? (
              <DonationIcon sx={{ fontSize: 20 }} />
            ) : (
              <SaleIcon sx={{ fontSize: 20 }} />
            )}
          </Box>
          <Box sx={{ textAlign: "left" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: colors.slate[900],
                fontSize: "1.15rem",
                lineHeight: 1.2,
              }}
            >
              {isDonation ? "Redeem Gift Voucher" : "Purchase Gift Card"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.slate[500], fontSize: "0.78rem" }}
            >
              {isDonation
                ? "Claim promotional tokens for free"
                : "Instant non-custodial token transfer"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: colors.slate[200] }} />

        {/* State Alerts */}
        {isSold && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: borderRadiusPx.lg,
              bgcolor: colors.amber[50],
              color: colors.amber[900],
              border: `1px solid ${colors.amber[200]}`,
              fontWeight: 600,
            }}
          >
            This gift card has already been claimed or sold out.
          </Alert>
        )}

        {!isSold && isExpired && (
          <Alert
            severity="error"
            sx={{
              borderRadius: borderRadiusPx.lg,
              bgcolor: colors.red[50],
              color: colors.red[900],
              border: `1px solid ${colors.red[200]}`,
              fontWeight: 600,
            }}
          >
            This gift card has expired and can no longer be redeemed.
          </Alert>
        )}

        {/* Order Summary Breakdown Box */}
        <Box
          sx={{
            p: 2,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.slate[50],
            border: `1px solid ${colors.slate[200]}`,
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="body2"
                sx={{ color: colors.slate[600], fontSize: "0.875rem" }}
              >
                Tokens to Receive
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: colors.slate[900],
                  fontSize: "0.95rem",
                }}
              >
                {tokenValue}{" "}
                <span style={{ color: colors.primary.main, fontWeight: 700 }}>
                  {tokenName}
                </span>
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="body2"
                sx={{ color: colors.slate[600], fontSize: "0.875rem" }}
              >
                Card Cost
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: isDonation ? colors.emerald[600] : colors.slate[900],
                  fontSize: "0.95rem",
                }}
              >
                {isDonation ? "FREE (0.00)" : `${price} ${currency}`}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="caption"
                sx={{ color: colors.slate[500], fontSize: "0.8rem" }}
              >
                Network Gas Fee
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: colors.slate[600], fontWeight: 600, fontSize: "0.8rem" }}
              >
                Standard Web3 Gas
              </Typography>
            </Stack>

            <Divider sx={{ my: 0.5, borderColor: colors.slate[200] }} />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color: colors.slate[900] }}
              >
                Total Payable
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  color: isDonation ? colors.emerald[700] : colors.slate[900],
                }}
              >
                {isDonation ? "0.00 (Gas only)" : `${price} ${currency}`}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Voucher Code Form Section (for donation cards) */}
        {isDonation && !isActionDisabled && (
          <Box sx={{ textAlign: "left" }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: colors.slate[800],
                mb: 1,
                fontSize: "0.875rem",
              }}
            >
              Voucher Code <span style={{ color: colors.red[600] }}>*</span>
            </Typography>

            <CommonInput
              fullWidth
              size="small"
              placeholder="Enter secret voucher code"
              value={voucherCode}
              onChange={(e) => onVoucherCodeChange(e.target.value)}
              error={isVoucherInvalid}
              helperText={isVoucherInvalid ? voucherErrorMessage : ""}
              startAdornment={
                <InputAdornment position="start">
                  <VoucherIcon sx={{ color: colors.slate[400], fontSize: 18 }} />
                </InputAdornment>
              }
              endAdornment={
                voucherCode ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => onVoucherCodeChange("")}
                      sx={{ p: 0.25 }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          </Box>
        )}

        {/* Action Button */}
        {!account ? (
          <CommonButton
            variant="primary"
            size="large"
            fullWidth
            onClick={onConnectWallet}
            startIcon={<WalletIcon />}
            sx={{
              py: 1.5,
              fontWeight: 800,
              fontSize: "0.95rem",
              boxShadow: customShadows.buttonPrimary,
            }}
          >
            Connect Wallet
          </CommonButton>
        ) : (
          <CommonButton
            variant={isDonation ? "contained" : "primary"}
            size="large"
            fullWidth
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={isActionDisabled || isSubmitting}
            endIcon={<ArrowRight size={18} />}
            sx={{
              py: 1.5,
              fontWeight: 800,
              fontSize: "0.95rem",
              bgcolor: isDonation && !isActionDisabled ? colors.amber[600] : undefined,
              "&:hover": {
                bgcolor: isDonation && !isActionDisabled ? colors.amber[700] : undefined,
              },
              boxShadow: customShadows.buttonPrimary,
            }}
          >
            {isSubmitting
              ? "Submitting Transaction..."
              : isDonation
              ? "Redeem Gift Card Free"
              : `Buy for ${price} ${currency}`}
          </CommonButton>
        )}

        {/* Security & Disclaimer Footer */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <LockIcon sx={{ fontSize: 14, color: colors.slate[400] }} />
          <Typography
            variant="caption"
            sx={{ color: colors.slate[500], fontSize: "0.75rem" }}
          >
            Verified on-chain smart contract escrow
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};

GiftCardPurchaseCard.propTypes = {
  isDonation: PropTypes.bool,
  tokenValue: PropTypes.string,
  tokenName: PropTypes.string,
  price: PropTypes.string,
  currency: PropTypes.string,
  voucherCode: PropTypes.string,
  onVoucherCodeChange: PropTypes.func.isRequired,
  isVoucherInvalid: PropTypes.bool,
  voucherErrorMessage: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  isSold: PropTypes.bool,
  isExpired: PropTypes.bool,
  account: PropTypes.string,
  onConnectWallet: PropTypes.func.isRequired,
};

export default GiftCardPurchaseCard;
